/** @jsxImportSource @emotion/react */

import React from 'react'
import { View, ActionButton, TextField, ErrorText } from '@components/atoms'
import { TabBoxView, UserList, Modal } from '@components/molecules'
import { AccessGroupList } from '@components/systems'

import {
	useAppSelector,
	useAppDispatch,
	SecetionUserListType,
	accessControlActions,
	ApiGetDataStatus,
} from '@redux/index'
import { emptyGroup } from '@redux/store/admin/accessControl.store'
import { grey, blue } from '@mui/material/colors'
import { UserType, AccessGroupType } from '@schemas'
import { produce } from 'immer'

export const AccessGroupTab = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const selectedTab: string = useAppSelector(state => state.accessControl.selectedTab)
	const groupList: AccessGroupType[] = useAppSelector(
		state => state.accessControl.groupList,
	)
	const userList: UserType[] = useAppSelector(state => state.accessControl.userList)
	const groupListStatus: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.groupListStatus,
	)
	const userListStatus: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.userListStatus,
	)
	const openedUserCateg: string[] = useAppSelector(
		state => state.accessControl.openedUserCateg,
	)
	const userSectionList: SecetionUserListType[] = useAppSelector(
		state => state.accessControl.userSectionList,
	)
	const selectedUser: UserType = useAppSelector(
		state => state.accessControl.selectedAccessUser,
	)
	const selectedGroup: AccessGroupType = useAppSelector(
		state => state.accessControl.selectedAccessGroup,
	)

	// state
	const [tabIndex, setTabIndex] = React.useState<number>(0)
	const [showModal, setShowModal] = React.useState<boolean>(false)
	const [errorMessage, setErrorMessage] = React.useState<string>('')

	React.useEffect(() => {
		const newTabIndex: number = titleTabList.findIndex(aTab => aTab.id === selectedTab)
		setTabIndex(newTabIndex)
	}, [selectedTab])

	const onGroupClick = React.useCallback((aGroup: AccessGroupType) => {
		reduxDispatch(accessControlActions.GROUP_ACCESS_CONTROL_STATUS('loading'))
		reduxDispatch(
			accessControlActions.SELECTED_ACCESS_GROUP({
				...aGroup,
			}),
		)
	}, [])

	const onUserClick = React.useCallback((aUser: UserType) => {
		reduxDispatch(accessControlActions.USER_ACCESS_CONTROL_STATUS('loading'))
		reduxDispatch(accessControlActions.SELECTED_ACCESS_USER({ ...aUser }))
	}, [])

	const onTabChange = (index: number) => {
		const newSelectedTabId: string = titleTabList[index].id
		reduxDispatch(accessControlActions.SELECTED_TAB(newSelectedTabId))
	}

	const onCollapsOpen = React.useCallback(
		(id: string) => {
			const newOpenedUserCateg: string[] = produce(openedUserCateg, draft => {
				const idx: number = openedUserCateg.indexOf(id)
				if (idx === -1) {
					draft.push(id)
				} else {
					draft.splice(idx, 1)
				}
			})

			reduxDispatch(accessControlActions.OPENED_USER_CATEG([...newOpenedUserCateg]))
		},
		[openedUserCateg],
	)

	const onCloseModal = React.useCallback(() => {
		setShowModal(false)
	}, [])

	const onModalSubmit = React.useCallback(() => {
		if (selectedGroup.name === '') {
			setErrorMessage('이름을 입력해주세요.')
		} else {
			const groupNameArr: string[] = groupList.reduce(
				(newGroupNameArr: string[], aPrevGroup: AccessGroupType) => {
					if (aPrevGroup.id !== selectedGroup.id) {
						newGroupNameArr.push(aPrevGroup.name.toLowerCase())
					}

					return newGroupNameArr
				},
				[],
			)

			if (groupNameArr.includes(selectedGroup.name.toLowerCase())) {
				setErrorMessage('이미 존재하는 이름입니다.')
			} else {
				if (selectedGroup.id === 'new') {
					reduxDispatch(accessControlActions.ADD_GROUP(selectedGroup))
				} else {
					reduxDispatch(accessControlActions.UPDATE_GROUP(selectedGroup))
				}
				onCloseModal()
			}
		}
	}, [selectedGroup])

	const onGroupNameChange = React.useCallback(
		(e: any) => {
			const newName: string = e.target.value
			const newAddGroup = { ...selectedGroup, name: newName }
			reduxDispatch(accessControlActions.SELECTED_ACCESS_GROUP(newAddGroup))
		},
		[selectedGroup],
	)

	const onInputEnterSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			onModalSubmit()
		}
	}

	const onEditGroupClick = (aGroup: AccessGroupType) => {
		onGroupClick(aGroup)
		reduxDispatch(accessControlActions.SELECTED_ACCESS_GROUP(aGroup))
		setErrorMessage('')
		reduxDispatch(accessControlActions.SHOW_SIDE_MODAL(true))
		reduxDispatch(accessControlActions.LEFT_MODAL_TAB_INDEX(0))
	}

	const onAddGroupClick = React.useCallback(() => {
		const newGroup = { ...emptyGroup, id: 'new' }
		reduxDispatch(accessControlActions.SELECTED_ACCESS_GROUP(newGroup))
		setShowModal(true)
	}, [])

	const titleTabList = React.useMemo(
		() => [
			{
				index: 0,
				id: 'group',
				label: '그룹',
				panel: (
					<AccessGroupList
						groupList={groupList}
						status={groupListStatus}
						onGroupClick={onGroupClick}
						selectedGroupIds={[selectedGroup.id]}
						showAddButton={true}
						onAddButtonClick={onAddGroupClick}
						rightAccessory={(group, isChecked) => {
							return (
								<ActionButton
									className="listItemEdit"
									style={isChecked ? { background: blue[50] } : {}}
									tooltip="수정"
									buttonColor="info"
									onClick={() => onEditGroupClick(group)}
									iconName="dots-vertical"
									iconTextColor={isChecked ? blue['A400'] : grey[700]}
								/>
							)
						}}
					/>
				),
			},
			{
				index: 1,
				id: 'user',
				label: '사원',
				panel: (
					<View css={{ padding: '0 0.5rem' }}>
						<UserList
							status={userListStatus}
							userList={userList}
							userSectionList={userSectionList}
							onUserClick={onUserClick}
							openedUserCateg={openedUserCateg}
							onCollapsOpen={onCollapsOpen}
							selectedUserIds={[selectedUser.id]}
						/>
					</View>
				),
			},
		],
		[
			groupList,
			groupListStatus,
			selectedGroup,
			userList,
			userListStatus,
			userSectionList,
			openedUserCateg,
			onCollapsOpen,
			selectedUser,
		],
	)

	return (
		<>
			<TabBoxView tabIndex={tabIndex} tabList={titleTabList} onTabChange={onTabChange} />

			<Modal
				target="newGroup"
				title="새 그룹 생성"
				show={showModal}
				onCloseModal={onCloseModal}
				onSubmit={onModalSubmit}
				closeStatus="inherit"
				closeTitle="닫기"
				submitTitle="저장"
				submitStatus="primary"
				style={{ maxWidth: '50rem' }}
				isDisableDisimissionOnSubmit={true}
			>
				<View css={{ width: '30rem' }}>
					<TextField
						defaultValue=""
						label="이름"
						size="small"
						autoFocus={true}
						margin="dense"
						onChange={onGroupNameChange}
						onKeyUp={onInputEnterSubmit}
					/>

					{errorMessage !== '' && <ErrorText>{errorMessage}</ErrorText>}
				</View>
			</Modal>
		</>
	)
}
